import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_ME } from '../graphql/user';

// 認證狀態類型
const AuthActionTypes = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// 初始狀態
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.LOGIN: {
      console.log('AuthContext: User logged in:', action.payload.user.username);
      localStorage.setItem('token', action.payload.token);
      
      // 確保 user 物件有 id 屬性
      const userWithId = {
        ...action.payload.user,
        id: action.payload.user._id || action.payload.user.id
      };
      
      return {
        ...state,
        user: userWithId,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    }
    
    case AuthActionTypes.LOGOUT:
      console.log('AuthContext: User logged out');
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    
    case AuthActionTypes.UPDATE_USER: {
      console.log('AuthContext: User data updated');
      const updatedUser = {
        ...state.user,
        ...action.payload,
        id: action.payload._id || action.payload.id || state.user?.id
      };
      return {
        ...state,
        user: updatedUser
      };
    }
    
    case AuthActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case AuthActionTypes.SET_ERROR:
      console.log('AuthContext: Error set:', action.payload);
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case AuthActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

// Provider 組件
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const client = useApolloClient();

  // 登入函數
  const login = (userData) => {
    dispatch({ type: AuthActionTypes.LOGIN, payload: userData });
  };

  // 登出函數
  const logout = useCallback(() => {
    // 清空 Apollo Client 快取
    client.clearStore();
    dispatch({ type: AuthActionTypes.LOGOUT });
  }, [client]);

  // 更新使用者資料
  const updateUser = useCallback((userData) => {
    dispatch({ type: AuthActionTypes.UPDATE_USER, payload: userData });
  }, []);

  // 設定載入狀態
  const setLoading = useCallback((loading) => {
    dispatch({ type: AuthActionTypes.SET_LOADING, payload: loading });
  }, []);

  // 設定錯誤
  const setError = (error) => {
    dispatch({ type: AuthActionTypes.SET_ERROR, payload: error });
  };

  // 清除錯誤
  const clearError = () => {
    dispatch({ type: AuthActionTypes.CLEAR_ERROR });
  };

  // 檢查 token 是否有效並獲取使用者信息
  const checkAuthStatus = useCallback(async () => {
    if (!state.token) {
      return;
    }

    try {
      setLoading(true);
      console.log('AuthContext: Checking auth status with token');
      
      // 調用後端的 me query 獲取完整的使用者信息
      const { data, error } = await client.query({
        query: GET_ME,
        fetchPolicy: 'network-only', // 強制從網絡獲取，不使用緩存
        errorPolicy: 'all' // 即使有錯誤也返回部分數據
      });

      if (error) {
        console.error('AuthContext: Token validation failed:', error);
        logout();
        return;
      }

      if (data && data.me) {
        console.log('AuthContext: User info retrieved:', data.me.username);
        // 更新使用者信息，確保包含所有字段
        updateUser({
          ...data.me,
          id: data.me.id || data.me._id
        });
      } else {
        console.log('AuthContext: No user data returned');
        logout();
      }
    } catch (error) {
      console.error('AuthContext: Token validation failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [state.token, setLoading, logout, client, updateUser]);

  // 組件掛載時檢查認證狀態
  useEffect(() => {
    if (state.token && !state.user) {
      // 只有在有token但沒有使用者信息時才檢查
      checkAuthStatus();
    }
  }, [state.token, state.user, checkAuthStatus]);

  const value = {
    ...state,
    login,
    logout,
    updateUser,
    setLoading,
    setError,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 